import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Conversation } from 'src/graphql';

export const TOOLTIP_ENTER_DELAY = 500;
export const TOOLTIP_LEAVE_DELAY = 500;
const TOOLTIP_SWITCH_DELAY = 500;

const clearTooltipTimer = (timerRef: MutableRefObject<number | null>) => {
  if (timerRef.current) {
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};

/** Hover state for the conversation summary tooltip shown next to conversation links. */
export const useConversationTooltip = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const enterTimerRef = useRef<number | null>(null);
  const leaveTimerRef = useRef<number | null>(null);
  const pendingRef = useRef<{ anchor: HTMLElement; conversation: Conversation } | null>(null);

  const open = useCallback(
    (conversationItem: Conversation | undefined, anchor: HTMLElement) => {
      if (!conversationItem) {
        return;
      }

      clearTooltipTimer(leaveTimerRef);
      clearTooltipTimer(enterTimerRef);

      if (isOpen) {
        // Store pending data - keep current tooltip visible in place until switch delay passes
        pendingRef.current = { anchor, conversation: conversationItem };
        setIsOpen(false);

        enterTimerRef.current = window.setTimeout(() => {
          if (pendingRef.current) {
            setAnchorEl(pendingRef.current.anchor);
            setConversation(pendingRef.current.conversation);
            pendingRef.current = null;
            setIsOpen(true);
          }
        }, TOOLTIP_SWITCH_DELAY);
      } else {
        pendingRef.current = null;
        setAnchorEl(anchor);
        setConversation(conversationItem);

        enterTimerRef.current = window.setTimeout(() => {
          setIsOpen(true);
        }, TOOLTIP_ENTER_DELAY);
      }
    },
    [isOpen],
  );

  const close = useCallback(() => {
    clearTooltipTimer(enterTimerRef);
    clearTooltipTimer(leaveTimerRef);
    pendingRef.current = null;
    leaveTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      setAnchorEl(null);
      setConversation(null);
    }, TOOLTIP_LEAVE_DELAY);
  }, []);

  useEffect(() => {
    return () => {
      clearTooltipTimer(enterTimerRef);
      clearTooltipTimer(leaveTimerRef);
    };
  }, []);

  return {
    anchorEl,
    conversation,
    isVisible: isOpen && !!conversation && !!anchorEl,
    open,
    close,
  };
};
